import { Subscription } from "rxjs";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FaxDirection } from "src/app/enums/fax-direction";
import { FaxStatus } from "src/app/enums/fax-status";
import { IClientProfileInfo } from "src/app/models/client/client-info";
import { IFax } from "src/app/models/fax/fax";
import { IFaxFolder } from "src/app/models/fax/fax-folder";
import { IFaxesSearchParams } from "src/app/models/fax/faxes-search-params";
import { ClientService } from "src/app/services/client.service";
import { FaxService } from "src/app/services/fax.service";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private _numbersSubscription?: Subscription;
  clientProfileInfo: IClientProfileInfo | null = null;
  primaryClientNumber = "*missing*";
  isClientInfoLoading = true;

  outboundFaxes: IFax[] = [];
  inboundFaxes: IFax[] = [];
  isOutboundFaxesLoading = true;
  isInboundFaxesLoading = true;
  faxesPageNumber = "1";

  folders: IFaxFolder[] = [];
  foldersPageNumber = "1";

  private readonly _refreshIntervalMs = 20_000;
  private readonly _pageSize = "10";

  private _refreshIntervalId: NodeJS.Timeout | null = null;
  private _isClientProfileInfoLoaded = false;
  private _isPrimaryClientNumberLoaded = false;

  constructor(
    private readonly _faxService: FaxService,
    private readonly _clientService: ClientService,
    private readonly _router: Router
  ) {}

  getFaxStatusIconSrc(faxStatus: FaxStatus) {
    switch (faxStatus) {
      case FaxStatus.Success: {
        return "assets/img/icons/auth-success.svg";
      }
      case FaxStatus.Failed: {
        return "assets/img/icons/failed.svg";
      }
      default: {
        return "assets/img/icons/loading.svg";
      }
    }
  }

  navigateToFax(faxId: number) {
    this._router.navigate(["/faxes", faxId]);
  }

  ngOnInit() {
    this.getClientProfileInfo();
    this.getPrimaryClientNumber();
    this.getFaxes();
    this.getFolders();

    this._refreshIntervalId = setInterval(this.getFaxes, this._refreshIntervalMs);
  }

  private getClientProfileInfo() {
    this._clientService.getClientInfo().subscribe(clientInfo => {
      this.clientProfileInfo = clientInfo.profileInfo;
      this._isClientProfileInfoLoaded = true;
      this.updateClientInfoLoadingState();
    });
  }

  private getPrimaryClientNumber() {
    this._numbersSubscription = this._clientService.watchNumbers().subscribe(numbers => {
      const primaryNumber = numbers.find(n => n.isPrimary);
      if (primaryNumber) {
        this.primaryClientNumber = primaryNumber.number;
      }
      this._isPrimaryClientNumberLoaded = true;
      this.updateClientInfoLoadingState();
    });
  }

  private updateClientInfoLoadingState() {
    this.isClientInfoLoading = !this._isClientProfileInfoLoaded || !this._isPrimaryClientNumberLoaded;
  }

  private getFaxes() {
    this._faxService.getAll(this.getFaxesSearchParams(FaxDirection.Outbound)).subscribe(faxes => {
      this.outboundFaxes = faxes.records;
      this.isOutboundFaxesLoading = false;
    });

    this._faxService.getAll(this.getFaxesSearchParams(FaxDirection.Inbound)).subscribe(faxes => {
      this.inboundFaxes = faxes.records;
      this.isInboundFaxesLoading = false;
    });
  }

  private getFaxesSearchParams(faxDirection: FaxDirection): IFaxesSearchParams {
    return {
      direction: faxDirection,
      pageNumber: this.faxesPageNumber,
      pageSize: this._pageSize,
    };
  }

  private getFolders() {
    this._faxService
      .getFolders({ pageNumber: this.foldersPageNumber, pageSize: this._pageSize })
      .subscribe(folders => {
        this.folders = folders.records;
      });
  }

  ngOnDestroy(): void {
    this._numbersSubscription?.unsubscribe();
    if (this._refreshIntervalId !== null) {
      clearInterval(this._refreshIntervalId);
    }
  }
}
